'use client'

import type { ColumnDef } from '@tanstack/react-table'
import Link from 'next/link'
import { memo, useCallback, useMemo, useState } from 'react'

import { EditUserSheet } from './EditUserForm'
import {
  getListUsersQueryKey,
  listUsers,
  ListUsersParams,
  Tags,
  useGetUserFilterMetadata,
  UsersListItem,
} from '@api/orval/apiHooks'
import type { Any } from '@api/orvalFetcher'
import { textCenterClassName } from '@app/(dashboard)/accounts/components/AccountsTable'
import { FlagCountry } from '@components/Statistics/FlagCountry'
import { useVisibleRowsOutsideState } from '@components/ui/InfiniteTable/hooks/useVisibleRowsOutsideState'
import { createEditAction, createViewAction, TableActions } from '@components/ui/SimpleTable/TableActions'
import { TagsCell } from '@components/ui/Tags/TagCell'
import { PrimeChip } from '@features/primeMode/PrimeChip'
import { buttonVariants, Badge } from '@fp/ui-kit'
import { useRealtimeTableSocket } from '@hooks/useRealtimeTableSocket'
import { statusBadgeMapper } from '@lib/badgeMapper'
import { cn } from '@lib/cn'
import { IS_TRADIN, TAB_KEYS } from '@lib/constants'
import { getCsv } from '@lib/csv'
import { splitAndCapitalFirstLetter } from '@lib/textFormatters'
import { InfiniteTable } from '@ui/InfiniteTable/InfiniteTable'
import { ShowDataAndTooltip } from '@ui/ShowDataAndTooltip'

const getColumns = (
  setIsEditModalOpen: (open: boolean) => void,
  setUserId: (id: number) => void,
  tags?: Tags,
): Array<ColumnDef<UsersListItem>> => [
  {
    accessorKey: 'created_at',
    header: 'Created at',
    cell: ({ row }) => <ShowDataAndTooltip data={row.original.created_at} />,
    minSize: 100,
    maxSize: 100,
  },
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <Link
        href={`/users/${row.original.id}`}
        className={cn(buttonVariants({ variant: 'linkNoPadding' }), textCenterClassName)}
      >
        {row.original.name}
      </Link>
    ),
    minSize: 160,
    meta: { textAlign: 'center' },
  },
  {
    accessorKey: 'country_code',
    header: 'Country',
    cell: ({ row }) => <FlagCountry countryCode={row.original.country_code} />,
    size: 90,
  },
  {
    accessorKey: 'brand_name',
    header: 'Brand',
    cell: ({ row }) => (row.original.brand_name ? <Badge variant='outline'>{row.original.brand_name}</Badge> : '-'),
    minSize: 120,
    maxSize: 160,
    meta: { textAlign: 'center' },
  },
  {
    accessorKey: 'email',
    header: 'Email',
    minSize: 250,
    maxSize: 250,
    cell: ({ row }) => <div className='break-words max-w-[240px]'>{row.original.email}</div>,
  },
  {
    accessorKey: 'kycaid_verification_status',
    header: 'KYC',
    cell: ({ row }) => (
      <Badge variant={statusBadgeMapper.kycaid_verification_status![row.original.kycaid_verification_status]}>
        {splitAndCapitalFirstLetter(row.original.kycaid_verification_status)}
      </Badge>
    ),
    minSize: 160,
    maxSize: 250,
    meta: { textAlign: 'center' },
  },
  {
    accessorKey: 'tag_list',
    header: 'Tags',
    minSize: 200,
    maxSize: 200,
    cell: ({ row }) => (
      <TagsCell
        resourceId={row.original.id}
        initialTags={row.original.tag_list}
        resourceType='users'
        tagList={tags || []}
      />
    ),
    meta: {
      textAlign: 'center',
    },
  },
  // {
  //   accessorKey: 'rise_onboarding_status',
  //   header: 'Rise',
  //   cell: ({ row }) => (
  //     <Badge variant={statusBadgeMapper.rise_onboarding_status![row.original.rise_onboarding_status]}>
  //       Rise: {splitAndCapitalFirstLetter(row.original.rise_onboarding_status)}
  //     </Badge>
  //   ),
  //   minSize: 160,
  //   maxSize: 250,
  //   meta: { textAlign: 'center' },
  // },
  {
    accessorKey: 'phone_number_verification_status',
    header: 'Phone',
    cell: ({ row }) => (
      <Badge
        variant={statusBadgeMapper.phone_number_verification_status![row.original.phone_number_verification_status]}
      >
        {splitAndCapitalFirstLetter(row.original.phone_number_verification_status)}
      </Badge>
    ),
    minSize: 160,
    maxSize: 250,
    meta: { textAlign: 'center' },
  },
  {
    accessorKey: 'trading_accounts_count',
    header: 'Trading accounts',
    size: 100,
  },
  {
    accessorKey: 'payouts_count',
    header: 'Payouts',
    cell: ({ row }) => <span className='text-nowrap'>{row.original.payouts_count}</span>,
    size: 100,
  },
  ...(IS_TRADIN
    ? [
        {
          accessorKey: 'trust_profile_score',
          header: 'Trust score',
          cell: ({ row }) => <span className='text-nowrap'>{row.original.trust_profile_score ?? '-'}</span>,
          size: 100,
          meta: { textAlign: 'center' },
        } as ColumnDef<UsersListItem>,
      ]
    : []),
  {
    accessorKey: 'id',
    header: 'Actions',
    cell: ({ row }) => (
      <TableActions
        actions={[
          createEditAction(() => {
            setUserId(row.original.id)
            setIsEditModalOpen(true)
          }),
          createViewAction(`/users/${row.original.id}`),
        ]}
      />
    ),
    size: 100,

    maxSize: 100,
    meta: { textAlign: 'center', isHover: true },
  },
]

interface UsersTableProps {
  query: ListUsersParams
}

export const UsersTable = memo(({ query }: UsersTableProps) => {
  const { requiredToUpdateDictRef, visibleRows, setVisibleRows } = useVisibleRowsOutsideState()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [userId, setUserId] = useState<number | null>(null)

  const { data: filterMeta } = useGetUserFilterMetadata()

  const tagList = filterMeta?.fields.tags

  const columns = useMemo(
    () => getColumns(setIsEditModalOpen, setUserId, tagList),
    [setIsEditModalOpen, setUserId, tagList],
  )

  useRealtimeTableSocket({
    socketName: 'users',
    baseKey: getListUsersQueryKey(),
    visibleRows,
    requiredToUpdateDictRef,
    mapPayload: (p: Any) => p.data ?? p,
  })

  useRealtimeTableSocket({
    socketName: 'tags',
    visibleRows,
    requiredToUpdateDictRef,
    baseKey: getListUsersQueryKey(),
    mapPayload: (p: Any) => ({ ...p.data, id: p.data?.resource_id ?? p.data?.id }),
  })

  const getCsvHandle = useCallback(async () => {
    await getCsv(() => {}, {
      model: 'user',
      exporter: 'all_data',
      search_filter: query,
    })
  }, [query])

  return (
    <>
      <InfiniteTable<UsersListItem, ListUsersParams>
        getQueryKey={getListUsersQueryKey}
        columns={columns}
        fetchPage={listUsers}
        query={query}
        tableTitle='Users'
        titleSuffix={<PrimeChip />}
        showItemsCount
        filterArea={TAB_KEYS.USERS}
        csvExportingHandle={getCsvHandle}
        onVisibleRowsChange={setVisibleRows}
      />

      {typeof userId === 'number' && (
        <EditUserSheet
          userId={userId}
          isOpenOutside={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          onSuccess={async () => {}}
        />
      )}
    </>
  )
})
